import { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon,
  PersonAdd as PersonAddIcon,
  Assessment as AssessmentIcon,
  RequestQuote as RequestQuoteIcon,
  BarChart as BarChartIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  Gavel as GavelIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';
import { colors } from '../../theme';
import logo from '../../assets/logo-interbarge.png';

// Páginas
import Home from '../../pages/Home';
import Dashboard from '../../pages/Dashboard';
import SupplyMaster from '../../pages/SupplyMaster';
import ActiveSuppliers from '../../pages/ActiveSuppliers';
import PotentialSuppliers from '../../pages/PotentialSuppliers';
import Evaluation from '../../pages/Evaluation';
import Reports from '../../pages/Reports';
import RFQList from '../../pages/RFQList';
import RFQCreate from '../../pages/RFQCreate';
import RFQDetail from '../../pages/RFQDetail';
import RFQCompare from '../../pages/RFQCompare';
import ContractList from '../../pages/ContractList';
import ContractCreate from '../../pages/ContractCreate';
import ContractDetail from '../../pages/ContractDetail';
import ReceptionManager from '../../pages/ReceptionManager';

const drawerWidth = 280;
const drawerWidthClosed = 72;

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

const navigationItems: NavigationItem[] = [
  { text: 'Home', icon: <HomeIcon />, path: '/' },
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Supply Master', icon: <InventoryIcon />, path: '/supply-master' },
  { text: 'Proveedores actuales', icon: <StoreIcon />, path: '/active-suppliers' },
  { text: 'Contactos comerciales', icon: <PersonAddIcon />, path: '/potential-suppliers' },
  { text: 'Evaluación', icon: <AssessmentIcon />, path: '/evaluation' },
  { text: 'Compulsas & RFQ', icon: <RequestQuoteIcon />, path: '/rfq' },
  { text: 'Contratos LTP', icon: <GavelIcon />, path: '/contracts' },
  { text: 'Recepción', icon: <LocalShippingIcon />, path: '/reception' },
  { text: 'Reportes', icon: <BarChartIcon />, path: '/reports' },
];

const MainLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawer = (isOpen: boolean) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box
        sx={{
          p: isOpen ? 3 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.background.sidebar,
          minHeight: '80px',
        }}
      >
        {isOpen ? (
          <img
            src={logo}
            alt="Interbarge"
            style={{ 
              maxWidth: '180px', 
              height: 'auto',
              filter: 'brightness(0) invert(1)',
            }}
          />
        ) : (
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.5rem' }}>
            IB
          </Typography>
        )}
      </Box>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Toggle Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <IconButton 
          onClick={handleSidebarToggle}
          sx={{ 
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
          }}
        >
          {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Menu Items */}
      <List sx={{ pt: 2, px: isOpen ? 2 : 1, flexGrow: 1 }}>
        {navigationItems.map((item) => {
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  borderRadius: 2,
                  color: 'white',
                  backgroundColor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(255, 255, 255, 0.08)',
                  },
                  py: 1.5,
                  justifyContent: isOpen ? 'flex-start' : 'center',
                  px: isOpen ? 2 : 1,
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: 'white', 
                    minWidth: isOpen ? 40 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isOpen && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: isActive ? 600 : 400,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {isOpen && (
        <>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* User Info */}
          <Box sx={{ p: 2, color: 'white' }}>
            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mb: 0.5 }}>
              Demo User
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Supply Chain Manager
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: colors.background.default }}>
      {/* Mobile Menu Button */}
      {!isHomePage && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            display: { xs: 'block', sm: 'none' },
            backgroundColor: colors.brand.blue,
            color: 'white',
            '&:hover': {
              backgroundColor: colors.brand.red,
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar - Mobile */}
      {!isHomePage && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: colors.background.sidebar,
            },
          }}
        >
          {drawer(true)}
        </Drawer>
      )}

      {/* Sidebar - Desktop */}
      {!isHomePage && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarOpen ? drawerWidth : drawerWidthClosed,
              transition: 'width 0.3s ease',
              backgroundColor: colors.background.sidebar,
              borderRight: 'none',
            },
          }}
          open
        >
          {drawer(sidebarOpen)}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: isHomePage 
            ? '100%' 
            : { sm: `calc(100% - ${sidebarOpen ? drawerWidth : drawerWidthClosed}px)` },
          ml: isHomePage 
            ? 0 
            : { sm: `${sidebarOpen ? drawerWidth : drawerWidthClosed}px` },
          transition: 'margin 0.3s ease, width 0.3s ease',
          minHeight: '100vh',
          p: isHomePage ? 0 : 3,
        }}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/supply-master" element={<SupplyMaster />} />
          <Route path="/active-suppliers" element={<ActiveSuppliers />} />
          <Route path="/potential-suppliers" element={<PotentialSuppliers />} />
          <Route path="/evaluation" element={<Evaluation />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/rfq" element={<RFQList />} />
          <Route path="/rfq/new" element={<RFQCreate />} />
          <Route path="/rfq/:id" element={<RFQDetail />} />
          <Route path="/rfq/:id/compare" element={<RFQCompare />} />
          <Route path="/contracts" element={<ContractList />} />
          <Route path="/contracts/new" element={<ContractCreate />} />
          <Route path="/contracts/:id" element={<ContractDetail />} />
          <Route path="/reception" element={<ReceptionManager />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default MainLayout;
